import api from '@/api';
import type { DataWrapper, Paginated } from '@/types/api';
import type { Brand, OwnedMotorcycle, VariantType } from '@/types/product';
import { ApiFactory } from './base/ApiFactory';

/**
 * Product data type matching ProductResource.php structure
 */
export interface Product {
    id: number;
    name: string;
    code: string;
    basePrice: number;
    salePrice?: number;
    isOnSale: boolean;
    imageUrl?: string;
    estimatedDownPayment: number;
    estimatedMonthlyPayment: number;
    productType?: string;
    brand: Brand;
    variants?: ProductVariant[];
    images: string[];
    variantTypes?: VariantType[];
}

/**
 * Product variant option data type
 */
export interface ProductVariantOption {
    type: string;
    type_id: number;
    value: string;
    value_id: number;
}

/**
 * Product variant data type
 */
export interface ProductVariant {
    sku: string;
    id: number;
    product_id: number;
    name: string;
    price: number;
    basePrice?: number;
    salePrice?: number;
    options?: ProductVariantOption[];
}

/**
 * Product detail data type matching ProductDetailResource.php structure
 */
export interface ProductDetail {
    id: number;
    name: string;
    code: string;
    description: string;
    basePrice: number;
    salePrice: number;
    estimatedDownPayment: number;
    estimatedMonthlyPayment: number;
    isOnSale: boolean;
    isRepossessed: boolean;
    isFeatured: boolean;
    status: number;
    brand: Brand;
    images: { id: number; url: string }[];
    specification?: Record<string, string>;
    variants: ProductVariant[];
    variantTypes?: VariantType[];
    productType?: string;
}

/**
 * Product variants response data type
 */
export interface ProductVariantsResponse {
    variants: ProductVariant[];
}

/**
 * Service class for interacting with the Product API endpoints.
 * Handles fetching product data, featured products, and product variants.
 */
export class ProductService extends ApiFactory<Product> {
    /**
     * Creates an instance of ProductService.
     */
    constructor() {
        super('/products', api);
    }

    /**
     * Retrieves a list of all products.
     * @param params - Optional query parameters for filtering/pagination
     * @returns Promise with products list
     */
    async list(params?: Record<string, any>): Promise<Paginated<Product>> {
        return this.get<Paginated<Product>>(`${this.resource}/list`, params);
    }

    /**
     * Retrieves a list of featured products.
     * @param params - Optional query parameters
     * @returns Promise with featured products list
     */
    async featured(params?: Record<string, any>): Promise<Paginated<Product>> {
        return this.get<Paginated<Product>>(`${this.resource}/featured`, params);
    }

    /**
     * Retrieves details of a specific product.
     * @param productId - The ID of the product
     * @returns Promise with product details
     */
    async show(productId: number | string): Promise<DataWrapper<ProductDetail>> {
        return this.get<DataWrapper<ProductDetail>>(`${this.resource}/${productId}`);
    }

    /**
     * Retrieves details of a specific product silently (without loading indicators).
     * @param productId - The ID of the product
     * @returns Promise with product details
     */
    async showSilent(productId: number | string): Promise<DataWrapper<ProductDetail>> {
        return this.get<DataWrapper<ProductDetail>>(`${this.resource}/${productId}`, undefined, {
            headers: { 'Skip-Loader': true },
        });
    }

    /**
     * Retrieves variants of a specific product.
     * @param productId - The ID of the product
     * @param params - Optional query parameters
     * @returns Promise with product variants list
     */
    async variants(
        productId: number | string,
        params?: Record<string, unknown>,
    ): Promise<{ data: ProductVariant[] }> {
        return this.get<{ data: ProductVariant[] }>(
            `${this.resource}/${productId}/variants`,
            params,
        );
    }

    /**
     * Retrieves variant types for a specific product.
     * @param productId - The ID of the product
     * @returns Promise with product variant types list
     */
    async variantTypes(
        productId: number | string,
    ): Promise<{ data: Array<{ id: number; name: string; display_name: string }> }> {
        return this.get<{ data: Array<{ id: number; name: string; display_name: string }> }>(
            `${this.resource}/${productId}/variant-types`,
        );
    }

    /**
     * Retrieves the user's owned motorcycles.
     * @param params - Optional query parameters
     * @returns Promise with list of owned motorcycles
     */
    async getOwned(params?: Record<string, any>): Promise<{ data: OwnedMotorcycle[] }> {
        return this.get<{ data: OwnedMotorcycle[] }>('/motorcycles/owned', params);
    }
}

export const productService = new ProductService();
export default ProductService;
